/**
 * PC-v2.2-C2.2 — «EXPORTAR EXCEL» DE INDICADORES Y GRÁFICOS
 *
 * Un botón que descarga, con la sesión, el XLSX de la vista con sus filtros actuales (`ruta` ya lleva periodo,
 * cliente y centro). Qué cifras salen lo decide el servidor (permisos, ámbito y configuración del portal): el navegador
 * sólo dice qué vista y qué filtros. Mismo camino que el resto de descargas (`api.fichero` + `guardarFichero`): nunca
 * una URL pública del fichero.
 *
 *   · Un clic mientras se genera no lanza otra descarga (el botón se deshabilita y, además, un cerrojo en memoria por
 *     si llegan dos clics antes de pintar); el servidor también rechaza la segunda (429).
 *   · Si cambian los filtros (o se sale de la vista) mientras se genera, esa descarga se cancela y su resultado se
 *     descarta: nunca se guarda el Excel de unos filtros que ya no están en pantalla.
 *   · Si falla, el mensaje del servidor (o «sin conexión») junto al botón; no se guarda nada a medias.
 *   · Al terminar, un aviso discreto con el nombre del fichero (`role="status"`), que se borra al cambiar los filtros.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { api } from '../api.ts';
import { guardarFichero } from '../paginas/exportaciones.tsx';
import { Error as ErrorCaja } from './base.tsx';

export function BotonExportarExcel({ ruta, motivoDeshabilitado = null, etiqueta = 'Exportar Excel', ayuda = 'Descarga un Excel con lo que ves, con los mismos filtros' }: {
  /** La ruta de exportación con los filtros de la vista, o null si todavía no se puede pedir. */
  ruta: string | null;
  /** Por qué no se puede exportar ahora (periodo inválido, configuración cargando…). */
  motivoDeshabilitado?: string | null;
  /** PC-v2.2-C2.3-B: el texto del botón y su ayuda, cuando hay más de un Excel en la misma pantalla. */
  etiqueta?: string;
  ayuda?: string;
}): ReactNode {
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  /** La descarga en curso (una como mucho): su cancelación es también el cerrojo contra el doble clic. */
  const enCurso = useRef<AbortController | null>(null);
  useEffect(() => {
    setHecho(null);
    setError(null);
    setGenerando(false);
    return () => {
      enCurso.current?.abort();
      enCurso.current = null;
    };
  }, [ruta]);
  async function exportar(): Promise<void> {
    if (ruta === null || enCurso.current !== null) return;
    const control = new AbortController();
    enCurso.current = control;
    setError(null);
    setHecho(null);
    setGenerando(true);
    try {
      const f = await api.fichero(ruta, control.signal);
      if (control.signal.aborted) return;
      guardarFichero(f.blob, f.nombre);
      setHecho(`Descargado: ${f.nombre}`);
    } catch (e) {
      if (!control.signal.aborted) setError(e);
    } finally {
      if (enCurso.current === control) {
        enCurso.current = null;
        setGenerando(false);
      }
    }
  }
  const deshabilitado = ruta === null || motivoDeshabilitado !== null;
  return (
    <div className="exportar-excel">
      <button
        type="button" onClick={() => void exportar()} disabled={deshabilitado || generando} aria-busy={generando}
        title={motivoDeshabilitado ?? ayuda}
      >
        {generando ? 'Generando Excel…' : etiqueta}
      </button>
      <span className="pequena tenue" role="status" aria-live="polite">{hecho ?? ''}</span>
      {error !== null && <ErrorCaja error={error} />}
    </div>
  );
}
